import { useQuery } from '@tanstack/vue-query';
import type { Tables } from '~/types/database.types';

// Extend the InstagramPost type to include randomRotation
type InstagramPostWithRotation = Tables<'instagram_media'> & {
  randomRotation: string;
};

// Helper function to generate a random rotation class
const generateRotationClass = (index: number) => {
  const baseAngle = 6; // Base rotation degrees
  const randomOffset = Math.floor(Math.random() * 7) - 3; // Random value from -3 to 3 (inclusive)
  const finalAngle = baseAngle + randomOffset;

  if (index % 2 === 0) {
    return `rotate-[${finalAngle}deg]`; // Even index: rotate clockwise
  } else {
    return `-rotate-[${finalAngle}deg]`; // Odd index: rotate counter-clockwise
  }
};

export const useSocialsData = (limit: number = 8) => {
  const supabase = useSupabaseClient<Database>();

  // Fetch Instagram posts for socials section
  const {
    data: instagramPosts,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['socialsInstagramPosts', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instagram_media')
        .select('*')
        .neq('media_type', 'VIDEO') // Exclude videos
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Map data to include randomRotation
      return (data as Tables<'instagram_media'>[]).map((post, index) => ({
        ...post,
        randomRotation: generateRotationClass(index),
      })) as InstagramPostWithRotation[];
    },
  });

  return {
    instagramPosts,
    error,
    isLoading,
  };
};
